import { useEffect, useState } from 'react';

/**
 * The licence and attribution page.
 *
 * This page is the deliverable for three obligations at once:
 *
 *  - OGL section 10 — a copy of the licence ships with the Open Game Content
 *    the app distributes. It must be reachable without logging in.
 *  - OGL section 6 — the COPYRIGHT NOTICE is reproduced exactly. It is
 *    rendered from the server verbatim; never summarise or re-typeset it.
 *  - The ruleset's Compatibility-Statement License section 1 — the trademark
 *    sentence appears on the first page that mentions the ruleset, which is
 *    this one.
 *
 * The ruleset's trademarks appear here as body text identifying the rules
 * source. They are Product Identity and must never brand the product, the UI
 * chrome, a page title or the repository.
 */

interface Licence {
  openGameLicense: string;
  openGameContentDesignation: string;
  compatibilityStatementLicense: string;
  copyrightNotices: string[];
  trademarkSentence: string;
  artworkExcluded: string;
}

export function LicencePage() {
  const [licence, setLicence] = useState<Licence | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    fetch('/api/licence', { signal: controller.signal })
      .then((res) => {
        if (!res.ok) throw new Error(`licence request failed: ${res.status}`);
        return res.json() as Promise<Licence>;
      })
      .then(setLicence)
      .catch((err: unknown) => {
        if (err instanceof Error && err.name === 'AbortError') return;
        setError(err instanceof Error ? err.message : String(err));
      });
    return () => controller.abort();
  }, []);

  if (error) {
    return (
      <section className="space-y-2">
        <h1 className="text-2xl font-semibold">Licence</h1>
        <p className="text-red-700 dark:text-red-400">Could not load the licence: {error}</p>
        <p>
          <a className="underline" href="/api/licence.txt">
            Read the plain-text licence instead
          </a>
        </p>
      </section>
    );
  }

  if (!licence) {
    return <p className="text-neutral-600 dark:text-neutral-400">Loading licence…</p>;
  }

  return (
    <article className="space-y-8">
      <header className="space-y-3">
        <h1 className="text-2xl font-semibold tracking-tight">Licence &amp; attribution</h1>
        <p className="text-neutral-700 dark:text-neutral-300">
          The rules content in this application — tables, stat blocks, spell descriptions and rules
          text — is Open Game Content, used under the Open Game License v1.0a and reproduced in
          full below.
        </p>
        {/* CSL section 1: mandatory on the first page mentioning the ruleset. */}
        <p className="text-neutral-700 dark:text-neutral-300">{licence.trademarkSentence}</p>
        <p className="text-neutral-700 dark:text-neutral-300">{licence.artworkExcluded}</p>
      </header>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold">Copyright notice</h2>
        {/* OGL section 6 requires the exact text. Rendered as supplied. */}
        <ul className="space-y-2">
          {licence.copyrightNotices.map((notice) => (
            <li key={notice} className="text-sm leading-relaxed">
              {notice}
            </li>
          ))}
        </ul>
      </section>

      <LegalText title="Open Game Content designation" body={licence.openGameContentDesignation} />
      <LegalText title="Open Game License v1.0a" body={licence.openGameLicense} />
      <LegalText title="Compatibility-Statement License" body={licence.compatibilityStatementLicense} />

      <p className="text-sm">
        <a className="underline" href="/api/licence.txt">
          Download as plain text
        </a>
      </p>
    </article>
  );
}

function LegalText({ title, body }: { title: string; body: string }) {
  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold">{title}</h2>
      <div className="max-w-none space-y-3 text-sm leading-relaxed">
        {body.split('\n\n').map((para, i) => (
          <p key={i} className="whitespace-pre-wrap">
            {para}
          </p>
        ))}
      </div>
    </section>
  );
}
